














































































import { Mixins, Component, Inject } from "vue-property-decorator";
// Types
import {
  ICProdProvider,
  ICProdProviderMethods
} from "@/models/providers/contractProduct";
import Context from "@/mixins/context";
import _ from "@/boot/lodash";
import {
  ContractStatusCodes as CSC,
  ContractStatusMapped
} from "@/models/contracts";
import { BACKEND_DATE_FORMAT } from "@/data/date";

@Component
export default class CProdTimeline extends Mixins(Context) {
  // Inject
  @Inject({ from: "contractProductProvider" })
  public readonly $cProdData!: () => ICProdProvider;
  @Inject({ from: "contractProductProviderMethods" })
  public readonly $cProdMethods!: ICProdProviderMethods;
  // Data
  public showCollapsableEvents = false;
  // Computed
  public get cProdData() {
    return this.$cProdData();
  }

  public get cProd() {
    return this.cProdData.contractProduct;
  }

  public get events() {
    const events: any[] = [];
    const status = this.cProdData.status.code;
    if (status === CSC.CLOSED) {
      // Terminated
      events.push(this.event_cancelled, this.event_terminated);
    } else if (status === CSC.CANCELLED) {
      // Cancelled
      events.push(
        this.scheduled_nextPayment,
        this.event_suspended,
        this.event_cancelled,
        this.scheduled_termination
      );
    } else if (this.cProdData.hasAutoExpireEnabled) {
      // Auto-Expires Enabled
      events.push(
        this.event_expiringSoon,
        this.scheduled_cancellation,
        this.scheduled_termination
      );
    } else if (status === CSC.SUSPENDED) {
      // Suspended
      events.push(
        this.scheduled_nextRenewal,
        this.scheduled_nextPayment,
        this.scheduled_nextInvoice,
        this.event_suspended,
        this.scheduled_cancellation,
        this.scheduled_termination
      );
    } else {
      // Active, Awaiting Activation, Pending
      events.push(
        this.event_notDue,
        this.event_autoRenewDisabled,
        this.scheduled_nextPayment,
        this.scheduled_nextInvoice,
        this.scheduled_nextRenewal,
        this.scheduled_suspension,
        this.scheduled_cancellation,
        this.scheduled_termination
      );
    }
    return _.orderBy(_.compact(events), ["date", "priority"], ["asc", "asc"]);
  }

  public get disableCollapse() {
    if (this.cProdData.hasAutoExpireEnabled) return true;
    return [CSC.CLOSED, CSC.CANCELLED, CSC.SUSPENDED].includes(
      this.cProdData.status.code as CSC
    );
  }

  public get collapsableEvents(): any[] {
    if (this.disableCollapse) return [];
    return _.filter(this.events, event => event.isCollapsable);
  }

  public get filteredEvents() {
    return _.filter(this.events, event => {
      if (this.disableCollapse) return true;
      if (!event.isCollapsable) return true;
      return this.showCollapsableEvents;
    });
  }

  public getCustomDateFromNow(moment: moment.Moment) {
    return moment.calendar(null, {
      ...((this.$t("_datetime.calendar") as unknown) as moment.CalendarSpec),
      sameElse: now => `[${moment.from(now as moment.Moment)}]`
    });
  }

  public get event_notDue() {
    if (this.cProdData.nextDueRenewalInvoice) return null;
    if (this.cProdData.nextDueOrderInvoice) return null;
    const date = this.cProd.start_date;
    const status = ContractStatusMapped(this.cProdData.status);
    return {
      date,
      icon: "info-circle",
      isCollapsable: false,
      label: status.name,
      type: status.type
    };
  }

  public get event_autoRenewDisabled() {
    if (this.cProdData.isPending) return null;
    if (!this.cProdData.hasAutoRenewDisabled) return null;
    const date = this.cProd.start_date;
    return {
      date,
      descPath: this.cProdData.canCreateNextInvoice
        ? "_sentence.cprod.event_auto_renew_disabled_create_next_invoice_msg"
        : "_sentence.cprod.event_auto_renew_disabled_msg",
      descSlots: [
        {
          name: "nextDueDate",
          is: "span",
          value: this.$moment
            .utc(this.cProd.next_due_date)
            .format(this.$t("_datetime.date.long") as string)
        },
        {
          name: "action",
          is: "u-link",
          value: this.$t("_action.click_here") as string,
          events: {
            click: () => this.$cProdMethods.createNextInvoice()
          }
        },
        {
          name: "turnOnAutoRenew",
          is: "u-link",
          value: (this.$t("_action.clicking_here") as string).toLowerCase(),
          events: {
            click: () => this.$cProdMethods.toggleAutoRenew(true)
          }
        }
      ],
      hasInput: false,
      icon: "toggle-off",
      isCollapsable: false,
      label: this.$t("_.auto_renew_disabled"),
      type: "is-warning"
    };
  }

  public get event_expiringSoon() {
    if (!this.cProdData.hasAutoExpireEnabled) return null;
    const moment = this.$moment();
    const date = moment.format(BACKEND_DATE_FORMAT);
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      descPath: "_sentence.cprod.event_auto_expire_enabled_msg",
      descSlots: [
        {
          name: "action",
          is: "u-link",
          value: (this.$t("_action.clicking_here") as string).toLowerCase(),
          events: {
            click: () => this.$cProdMethods.toggleAutoExpire(false)
          }
        }
      ],
      label: this.$t("_.expiring_soon"),
      hasInput: false,
      isCollapsable: false,
      type: "is-danger",
      icon: "hourglass-half"
    };
  }

  public get scheduled_nextRenewal() {
    if (this.cProdData.hasAutoRenewDisabled) return null;
    const date = this.cProd.next_due_date;
    const moment = this.$moment(date).endOf("day");
    if (!moment.isValid()) return null;
    const isFutureDate = moment > this.$moment();
    const isOverdue = [CSC.SUSPENDED].includes(
      this.cProdData.status.code as CSC
    );
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: isFutureDate
        ? this.isAdmin
          ? "_sentence.cprod.editable_renewal_date_msg"
          : "_sentence.cprod.readonly_renewal_date_msg"
        : "_sentence.cprod.event_renewal_overdue_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      hasInput:
        this.isAdmin && (isFutureDate || this.cProdData.canModifyBilling),
      icon: isOverdue
        ? "exclamation-triangle"
        : isFutureDate
        ? "calendar-alt"
        : "info-circle",
      iconPack: !isOverdue && isFutureDate ? "far" : "fas",
      isCollapsable: false,
      isEditable: this.cProdData.canModifyBilling,
      class: [isFutureDate && "has-dashed-appearance"],
      label: isOverdue
        ? this.$t("_.renewal_overdue")
        : this.$t("_.next_renewal"),
      priority: 2,
      type: isOverdue
        ? "is-danger"
        : isFutureDate
        ? "is-grey-light"
        : "is-info",
      onClick: () => this.$cProdMethods.openModifyProductModal()
    };
  }

  public get scheduled_nextInvoice() {
    if (this.cProdData.hasAutoRenewDisabled) return null;
    const date = this.cProd.next_invoice_date;
    const moment = this.$moment(date).endOf("day");
    const isFutureDate = moment > this.$moment();
    if (!moment.isValid()) return null;
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: this.cProdData.canCreateNextInvoice
        ? "_sentence.cprod.next_invoice_msg_with_link"
        : "_sentence.cprod.next_invoice_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        },
        {
          name: "action",
          is: "u-link",
          value: (this.$t("_action.click_here") as string).toLowerCase(),
          events: {
            click: () => this.$cProdMethods.createNextInvoice()
          }
        }
      ],
      label: this.$t("_.next_invoice"),
      hasInput: false,
      isCollapsable: false,
      isEditable: false,
      class: [isFutureDate && "has-dashed-appearance"],
      type: "is-grey-light",
      icon: "calendar-alt",
      iconPack: "far"
    };
  }

  public get scheduled_nextPayment() {
    const invoice =
      this.cProdData.nextDueRenewalInvoice ||
      this.cProdData.nextDueOrderInvoice;
    if (!invoice) return null;
    const date = _.get(invoice, "due_date");
    const moment = this.$moment(date).endOf("day");
    if (!moment.isValid()) return null;
    const isFutureDate = moment > this.$moment();
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: isFutureDate
        ? "_sentence.cprod.event_payment_due_msg"
        : "_sentence.cprod.event_payment_overdue_msg",
      descSlots: [
        {
          name: "invoiceNum",
          is: "u-invoice-link",
          value: invoice?.number,
          bind: {
            invoiceId: invoice?.id,
            class: "has-text-dark has-text-underlined"
          }
        },
        {
          name: "dueDate",
          value: dateFormatted
        }
      ],
      hasInput: false,
      icon: isFutureDate ? "info-circle" : "exclamation-triangle",
      isCollapsable: false,
      label: this.$t(isFutureDate ? "_.payment_due" : "_.payment_overdue"),
      priority: 1,
      type: isFutureDate ? "is-warning" : "is-danger"
    };
  }

  public get scheduled_suspension() {
    const date =
      this.cProd.not_suspend_until || this.cProd.calculated_suspend_date;
    const moment = this.$moment(date).endOf("day");
    const isFutureDate = moment > this.$moment();
    if (!moment.isValid()) return null;
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: this.isAdmin
        ? "_sentence.cprod.editable_suspension_date_msg"
        : "_sentence.cprod.readonly_suspension_date_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      label: this.$t("_.suspension"),
      hasInput: this.isAdmin,
      isCollapsable: true,
      class: [isFutureDate && "has-dashed-appearance"],
      type: "is-grey-light",
      icon: "calendar-alt",
      iconPack: "far",
      onClick: async () => {
        this.openSelectDateModal({
          title: this.$t("_sentence.cprod.set_suspension_date"),
          defaultDate: moment.toDate(),
          minDate: this.$moment(this.cProd.calculated_suspend_date)
            .startOf("day")
            .toDate(),
          onSelect: async date => {
            date = this.$moment(date).format(BACKEND_DATE_FORMAT);
            await this.$cProdMethods.changeDates({ not_suspend_until: date });
          }
        });
      }
    };
  }

  public get event_suspended() {
    const date = this.cProd.suspended_date;
    const moment = this.$moment(date);
    if (!moment.isValid()) return null;
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: "_sentence.cprod.event_suspended_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      hasInput: false,
      icon: "exclamation-triangle",
      isCollapsable: false,
      label: this.$t("_.suspended"),
      type: "is-danger"
    };
  }

  public get scheduled_cancellation() {
    const date =
      this.cProd.not_cancel_until || this.cProd.calculated_cancel_date;
    const moment = this.$moment(date).endOf("day");
    if (!moment.isValid()) return null;
    const isFutureDate = moment > this.$moment();
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: this.isAdmin
        ? "_sentence.cprod.editable_cancellation_date_msg"
        : "_sentence.cprod.readonly_cancellation_date_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      hasInput: this.isAdmin,
      isCollapsable: true,
      icon: "calendar-alt",
      iconPack: "far",
      label: this.$t("_.cancellation"),
      class: [isFutureDate && "has-dashed-appearance"],
      type: "is-grey-light",
      onClick: async () => {
        this.openSelectDateModal({
          title: this.$t("_sentence.cprod.set_cancellation_date"),
          defaultDate: moment.toDate(),
          minDate: this.$moment(this.cProd.calculated_cancel_date)
            .startOf("day")
            .toDate(),
          onSelect: async date => {
            date = this.$moment(date).format(BACKEND_DATE_FORMAT);
            await this.$cProdMethods.changeDates({ not_cancel_until: date });
          }
        });
      }
    };
  }

  public get event_cancelled() {
    const date = this.cProd.cancelled_date;
    const moment = this.$moment(date);
    if (!moment.isValid()) return null;
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: "_sentence.cprod.event_cancelled_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      label: this.$t("_.cancelled"),
      hasInput: false,
      isCollapsable: false,
      type: "is-grey-light",
      icon: "ban"
    };
  }

  public get scheduled_termination() {
    const date = this.cProd.not_close_until || this.cProd.calculated_close_date;
    const moment = this.$moment(date).endOf("day");
    if (!moment.isValid()) return null;
    const isFutureDate = moment > this.$moment();
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: this.isAdmin
        ? "_sentence.cprod.editable_termination_date_msg"
        : "_sentence.cprod.readonly_termination_date_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      label: this.$t("_.termination"),
      hasInput: this.isAdmin,
      isCollapsable: true,
      class: [isFutureDate && "has-dashed-appearance"],
      type: "is-grey-light",
      icon: "calendar-alt",
      iconPack: "far",
      onClick: async () => {
        this.openSelectDateModal({
          title: this.$t("_sentence.cprod.set_termination_date"),
          defaultDate: moment.toDate(),
          minDate: this.$moment(this.cProd.calculated_close_date)
            .startOf("day")
            .toDate(),
          onSelect: async date => {
            date = this.$moment(date).format(BACKEND_DATE_FORMAT);
            await this.$cProdMethods.changeDates({ not_close_until: date });
          }
        });
      }
    };
  }

  public get event_terminated() {
    const date = this.cProd.closed_date;
    const moment = this.$moment(date);
    if (!moment.isValid()) return null;
    const dateFormatted = moment.format(
      this.$t("_datetime.date.long") as string
    );
    return {
      date,
      dateFormatted,
      dateFromNow: this.getCustomDateFromNow(moment),
      descPath: "_sentence.cprod.event_terminated_msg",
      descSlots: [
        {
          name: "date",
          value: dateFormatted
        }
      ],
      label: this.$t("_.terminated"),
      hasInput: false,
      isCollapsable: false,
      type: "is-dark",
      icon: "times-circle"
    };
  }

  public openSelectDateModal(props) {
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import("@/components/app/global/date/selectDateModal.vue"),
        parent: this,
        props,
        width: 440
      }
    });
  }
}
